/**
 * Aspect interpretation engine.
 * Produces psychologically meaningful, chart-specific interpretations
 * instead of generic template output.
 */

const PLANET_PSYCHOLOGY = {
  sun: 'core identity, vitality, and creative will',
  moon: 'emotional needs, instinctive reactions, and internal safety',
  mercury: 'thinking, language, and how interpretation shapes reality',
  venus: 'attraction, pleasure, relational balance, and values',
  mars: 'drive, protection, and decisive action',
  jupiter: 'expansion, belief, and perspective',
  saturn: 'structure, responsibility, and maturation',
  uranus: 'disruption, originality, and liberation impulse',
  neptune: 'imagination, permeability, and symbolic sensitivity',
  pluto: 'deep transformation, control, and regeneration',
  northnode: 'growth direction and developmental edge',
  lilith: 'untamed instinct and uncompromised truth',
  chiron: 'wound-healing intelligence and restorative skill',
};

const ASPECT_DYNAMICS = {
  conjunction: {
    titleSuffix: 'Merged Focus',
    dynamic: 'fusion, concentration, and intensity',
    lived: 'These functions tend to operate as one; they reinforce each other and can amplify or overwhelm.',
    integration: 'Learning to distinguish the two functions while honoring their combined power.',
  },
  opposition: {
    titleSuffix: 'Polarity',
    dynamic: 'polarity, projection, and the need to balance two ends',
    lived: 'One function may be lived while the other is projected onto others or experienced as external pressure.',
    integration: 'Finding the middle ground where both poles can coexist without canceling each other.',
  },
  square: {
    titleSuffix: 'Developmental Friction',
    dynamic: 'tension, friction, and developmental pressure',
    lived: 'These functions push against each other, creating recurring friction that demands resolution.',
    integration: 'Using the tension as fuel for growth rather than as a source of repeated conflict.',
  },
  trine: {
    titleSuffix: 'Natural Flow',
    dynamic: 'ease, natural flow, and the risk of overreliance',
    lived: 'These functions support each other with little effort; the challenge is staying conscious rather than coasting.',
    integration: 'Honoring the gift without taking it for granted; bringing conscious choice to what flows easily.',
  },
  sextile: {
    titleSuffix: 'Supportive Opening',
    dynamic: 'opportunity, usable support, and conscious development',
    lived: 'These functions can work together when effort is applied; the connection is available but not automatic.',
    integration: 'Actively bridging the two functions rather than waiting for them to align on their own.',
  },
};

const SIGN_EXPRESSIONS = {
  Aries: 'direct initiation, impatience, and assertive self-assertion',
  Taurus: 'steadiness, embodiment, and resistance to sudden change',
  Gemini: 'curiosity, mental flexibility, and branching attention',
  Cancer: 'emotional memory, protectiveness, and sensitivity to atmosphere',
  Leo: 'visibility, pride, and heart-led creative risk',
  Virgo: 'precision, refinement, and attention to practical detail',
  Libra: 'relational calibration, harmony-seeking, and response to imbalance',
  Scorpio: 'depth, intensity, and psychological honesty',
  Sagittarius: 'meaning-seeking, expansion, and appetite for truth',
  Capricorn: 'restraint, responsibility, and strategic building',
  Aquarius: 'detachment, perspective-shifting, and nonconformist choice',
  Pisces: 'permeability, imagination, and symbolic processing',
};

const ASPECT_TITLE_PAIRS = {
  'moon-square-venus': 'Emotional Needs vs Relational Harmony',
  'moon-opposition-venus': 'Feeling and Relating in Polarity',
  'moon-trine-venus': 'Emotional and Relational Ease',
  'moon-sextile-venus': 'Emotional and Relational Support',
  'moon-conjunction-venus': 'Emotion and Attraction Merged',
  'sun-square-saturn': 'Identity and Structure in Friction',
  'sun-opposition-saturn': 'Identity and Responsibility in Polarity',
  'sun-trine-saturn': 'Identity and Structure in Flow',
  'sun-square-moon': 'Identity and Emotion in Tension',
  'sun-opposition-moon': 'Identity and Emotion in Polarity',
  'sun-opposition-jupiter': 'Identity and Expansion Out of Balance',
  'mercury-square-mars': 'Mind and Action in Tension',
  'mercury-trine-saturn': 'Structured Thinking and Clear Containment',
  'mercury-conjunction-venus': 'Mind and Values Merged',
  'venus-square-mars': 'Desire and Drive in Friction',
  'venus-opposition-mars': 'Attraction and Action in Polarity',
  'venus-trine-jupiter': 'Values and Expansion in Flow',
  'mars-square-saturn': 'Drive and Restraint in Conflict',
  'jupiter-opposition-saturn': 'Expansion and Limitation in Balance',
  'moon-square-saturn': 'Emotional Needs and Restraint in Friction',
  'moon-opposition-saturn': 'Emotion and Structure in Polarity',
  'sun-square-mars': 'Identity and Drive in Friction',
  'mercury-opposition-neptune': 'Mind and Imagination in Polarity',
  'venus-square-pluto': 'Values and Intensity in Friction',
  'mars-conjunction-pluto': 'Drive and Transformation Merged',
};

function getAspectTitleKey(p1, p2, type) {
  const a = String(p1 || '').toLowerCase();
  const b = String(p2 || '').toLowerCase();
  const t = String(type || 'conjunction').toLowerCase();
  const key1 = `${a}-${t}-${b}`;
  const key2 = `${b}-${t}-${a}`;
  return ASPECT_TITLE_PAIRS[key1] ? key1 : (ASPECT_TITLE_PAIRS[key2] ? key2 : key1);
}

function capitalizeType(type) {
  const t = String(type || '').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function getDynamicTitle(p1Label, p2Label, type, p1, p2) {
  const key = getAspectTitleKey(p1, p2, type);
  const custom = ASPECT_TITLE_PAIRS[key];
  const typeLabel = capitalizeType(type);
  if (custom) return `${p1Label} ${typeLabel} ${p2Label} — ${custom}`;
  const dyn = ASPECT_DYNAMICS[type]?.titleSuffix || 'relationship';
  return `${p1Label} ${typeLabel} ${p2Label} — ${dyn}`;
}

function orbStrength(orb) {
  const o = Math.abs(Number(orb) || 0);
  if (o <= 2) return { label: 'tight', phrase: 'especially pronounced' };
  if (o <= 5) return { label: 'moderate', phrase: 'clearly present' };
  return { label: 'wide', phrase: 'present but less dominant' };
}

function signModifier(sign, planet) {
  const expr = SIGN_EXPRESSIONS[sign];
  return expr ? `In ${sign}, this tends toward ${expr}.` : '';
}

export function generateAspectInterpretation({ aspect, p1, p2 }) {
  if (!aspect) return null;
  const type = String(aspect.type || 'conjunction').toLowerCase();
  const p1Key = String(aspect.p1 || '').toLowerCase();
  const p2Key = String(aspect.p2 || '').toLowerCase();
  const p1Label = aspect.p1Label || p1Key;
  const p2Label = aspect.p2Label || p2Key;
  const sign1 = p1?.sign || 'its sign';
  const sign2 = p2?.sign || 'its sign';
  const orb = Number(aspect.orb || 0);
  const orbInfo = orbStrength(orb);

  const planet1Psych = PLANET_PSYCHOLOGY[p1Key] || 'one core function';
  const planet2Psych = PLANET_PSYCHOLOGY[p2Key] || 'another core function';
  const aspectDyn = ASPECT_DYNAMICS[type] || ASPECT_DYNAMICS.conjunction;

  const sign1Expr = SIGN_EXPRESSIONS[sign1] || `${sign1} expression`;
  const sign2Expr = SIGN_EXPRESSIONS[sign2] || `${sign2} expression`;

  const title = getDynamicTitle(p1Label, p2Label, type, p1Key, p2Key);

  const paragraphs = [];

  paragraphs.push(
    `${p1Label} speaks to ${planet1Psych}; ${p2Label} to ${planet2Psych}. ` +
    `The ${type} tends to create ${aspectDyn.dynamic}: ${aspectDyn.lived}`
  );

  paragraphs.push(
    `${p1Label} in ${sign1} may express through ${sign1Expr.toLowerCase()}, while ` +
    `${p2Label} in ${sign2} moves through ${sign2Expr.toLowerCase()}. ` +
    `The aspect between them brings these two modes into relationship.`
  );

  paragraphs.push(
    `This pattern may show up in emotional habits, communication style, conflict dynamics, ` +
    `or how you balance different needs in relationships and work. ` +
    `With an orb of ${orb.toFixed(2)}°, the connection is ${orbInfo.phrase}.`
  );

  paragraphs.push(
    `Over time, this aspect often matures through ${aspectDyn.integration}`
  );

  return {
    title,
    paragraphs,
    orbInfo,
  };
}

export function sortAspectsByImportance(aspects, chart) {
  if (!aspects?.length) return [];
  const personal = new Set(['sun', 'moon', 'mercury', 'venus', 'mars']);
  const outer = new Set(['jupiter', 'saturn', 'uranus', 'neptune', 'pluto']);
  return [...aspects].sort((a, b) => {
    const orbA = Math.abs(Number(a.orb) || 10);
    const orbB = Math.abs(Number(b.orb) || 10);
    if (orbA !== orbB) return orbA - orbB;
    const p1A = String(a.p1 || '').toLowerCase();
    const p2A = String(a.p2 || '').toLowerCase();
    const p1B = String(b.p1 || '').toLowerCase();
    const p2B = String(b.p2 || '').toLowerCase();
    const personalA = (personal.has(p1A) ? 2 : 0) + (personal.has(p2A) ? 2 : 0);
    const personalB = (personal.has(p1B) ? 2 : 0) + (personal.has(p2B) ? 2 : 0);
    if (personalA !== personalB) return personalB - personalA;
    return 0;
  });
}
